class YgTimeBoxSwiper extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;

    this.cacheElements();
    if (!this.hasRequiredElements()) return;

    this.isInitialized = true;
    this.currentIndex = 0;
    this.plannedIndex = 0;
    this.offsets = [0];
    this.pendingIndexes = [];
    this.isProcessingMoves = false;
    this.rafId = null;
    this.initFrameId = null;
    this.moveAnimationId = null;
    this.resizeTimer = null;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.desktopQuery = window.matchMedia('(min-width: 768px)');
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.requestUpdate = this.requestUpdate.bind(this);
    this.scheduleMeasure = this.scheduleMeasure.bind(this);
    this.handlePreviousClick = this.handlePreviousClick.bind(this);
    this.handleNextClick = this.handleNextClick.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);

    this.previousButton.addEventListener('click', this.handlePreviousClick);
    this.nextButton.addEventListener('click', this.handleNextClick);
    this.viewport.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.viewport.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    window.addEventListener('scroll', this.requestUpdate, { passive: true });
    window.addEventListener('resize', this.scheduleMeasure, { passive: true });
    document.addEventListener('shopify:block:select', this.handleBlockSelect);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.scheduleMeasure);
      this.resizeObserver.observe(this.content);
    }

    this.initFrameId = requestAnimationFrame(() => {
      this.initFrameId = null;
      this.measure();
      this.update();
    });
  }

  cacheElements() {
    this.scrollSpace = this.querySelector('[data-scroll-space]');
    this.sticky = this.querySelector('[data-sticky]');
    this.content = this.querySelector('[data-content]');
    this.header = this.querySelector('[data-header]');
    this.viewport = this.querySelector('[data-viewport]');
    this.track = this.querySelector('[data-track]');
    this.previousButton = this.querySelector('[data-previous]');
    this.nextButton = this.querySelector('[data-next]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
  }

  hasRequiredElements() {
    return Boolean(
      this.scrollSpace &&
        this.sticky &&
        this.content &&
        this.header &&
        this.viewport &&
        this.track &&
        this.previousButton &&
        this.nextButton &&
        this.slides.length
    );
  }

  getScrollPerSlide() {
    const value = this.desktopQuery.matches
      ? this.dataset.scrollPerSlideDesktop
      : this.dataset.scrollPerSlideMobile;

    return Math.max(120, Number(value) || 480);
  }

  getSlidesPerView() {
    const value = parseFloat(getComputedStyle(this).getPropertyValue('--yg-slides-per-view'));

    return value > 0 ? value : 1;
  }

  getStickyTop() {
    return parseFloat(getComputedStyle(this.sticky).top) || 0;
  }

  measure() {
    this.track.style.transform = 'translate3d(0, 0, 0)';

    const slidesPerView = this.getSlidesPerView();
    const trackStyles = getComputedStyle(this.track);
    const cardGap = parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
    const visibleGapCount = Math.max(0, Math.ceil(slidesPerView) - 1);
    const cardWidth = (this.viewport.clientWidth - cardGap * visibleGapCount) / slidesPerView;

    this.style.setProperty('--yg-card-width', `${Math.max(0, cardWidth)}px`);

    const slideStep =
      this.slides.length > 1
        ? this.slides[1].offsetLeft - this.slides[0].offsetLeft
        : this.slides[0].offsetWidth;
    const maxTranslate = Math.max(0, this.track.scrollWidth - this.viewport.clientWidth);

    this.offsets = [0];

    if (slideStep > 0) {
      let nextOffset = slideStep;

      while (nextOffset < maxTranslate - 1) {
        this.offsets.push(nextOffset);
        nextOffset += slideStep;
      }
    }

    if (maxTranslate > 0 && Math.abs(this.offsets[this.offsets.length - 1] - maxTranslate) > 1) {
      this.offsets.push(maxTranslate);
    }

    const contentHeight = Math.ceil(this.content.getBoundingClientRect().height);
    const stickyTop = Math.max(0, Math.round((window.innerHeight - contentHeight) / 2));
    const travel = this.getScrollPerSlide() * Math.max(0, this.offsets.length - 1);
    const headerMarginBottom = parseFloat(getComputedStyle(this.header).marginBottom) || 0;
    const headerHeight = Math.ceil(this.header.getBoundingClientRect().height + headerMarginBottom);
    const media = this.slides[0].querySelector('[data-media]');
    const mediaHeight = media ? Math.ceil(media.offsetHeight) : 0;

    this.style.setProperty('--yg-sticky-top', `${stickyTop}px`);
    this.style.setProperty('--yg-sticky-height', `${contentHeight}px`);
    this.style.setProperty('--yg-header-height', `${headerHeight}px`);
    this.style.setProperty('--yg-media-height', `${mediaHeight}px`);
    this.scrollSpace.style.height = `${contentHeight + travel}px`;
  }

  update() {
    const lastIndex = this.offsets.length - 1;
    const rect = this.scrollSpace.getBoundingClientRect();
    const indexProgress = Math.min(
      lastIndex,
      Math.max(0, (this.getStickyTop() - rect.top) / this.getScrollPerSlide())
    );
    const lowerIndex = Math.floor(indexProgress);
    const upperIndex = Math.min(lastIndex, Math.ceil(indexProgress));
    const localProgress = indexProgress - lowerIndex;
    const lowerOffset = this.offsets[lowerIndex] || 0;
    const upperOffset = this.offsets[upperIndex] || lowerOffset;
    const translate = lowerOffset + (upperOffset - lowerOffset) * localProgress;

    this.track.style.transform = `translate3d(${-translate}px, 0, 0)`;
    this.currentIndex = Math.min(lastIndex, Math.max(0, Math.floor(indexProgress + 0.001)));
    this.previousButton.disabled = indexProgress <= 0.001;
    this.nextButton.disabled = indexProgress >= lastIndex - 0.001;
  }

  requestUpdate() {
    if (this.rafId) return;

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.update();
    });
  }

  scheduleMeasure() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.resizeTimer = null;
      this.measure();
      this.update();
    }, 80);
  }

  clampIndex(index) {
    return Math.min(this.offsets.length - 1, Math.max(0, index));
  }

  getScrollTopForIndex(index) {
    const scrollSpaceTop = window.scrollY + this.scrollSpace.getBoundingClientRect().top;

    return scrollSpaceTop - this.getStickyTop() + this.clampIndex(index) * this.getScrollPerSlide();
  }

  animateToIndex(index, onComplete) {
    const targetTop = this.getScrollTopForIndex(index);
    const startTop = window.scrollY;
    const distance = targetTop - startTop;

    if (this.moveAnimationId) {
      cancelAnimationFrame(this.moveAnimationId);
      this.moveAnimationId = null;
    }

    if (this.motionQuery.matches || Math.abs(distance) < 1) {
      window.scrollTo(0, targetTop);
      this.requestUpdate();
      this.moveAnimationId = requestAnimationFrame(() => {
        this.moveAnimationId = null;
        onComplete();
      });
      return;
    }

    const duration = 420;
    let startTime = null;

    const step = (timestamp) => {
      if (startTime === null) startTime = timestamp;

      const progress = Math.min(1, (timestamp - startTime) / duration);
      const easedProgress = 1 - Math.pow(1 - progress, 3);

      window.scrollTo(0, startTop + distance * easedProgress);

      if (progress < 1) {
        this.moveAnimationId = requestAnimationFrame(step);
        return;
      }

      this.moveAnimationId = null;
      window.scrollTo(0, targetTop);
      this.requestUpdate();
      onComplete();
    };

    this.moveAnimationId = requestAnimationFrame(step);
  }

  processPendingMoves() {
    if (!this.isInitialized || this.isProcessingMoves || !this.pendingIndexes.length) return;

    this.isProcessingMoves = true;
    const targetIndex = this.pendingIndexes.shift();

    this.animateToIndex(targetIndex, () => {
      this.currentIndex = targetIndex;
      this.isProcessingMoves = false;

      if (!this.pendingIndexes.length) {
        this.plannedIndex = this.currentIndex;
      }

      this.processPendingMoves();
    });
  }

  queueMove(direction) {
    const baseIndex =
      this.isProcessingMoves || this.pendingIndexes.length ? this.plannedIndex : this.currentIndex;
    const targetIndex = this.clampIndex(baseIndex + direction);

    if (targetIndex === baseIndex) return;

    this.plannedIndex = targetIndex;
    this.pendingIndexes.push(targetIndex);
    this.processPendingMoves();
  }

  handlePreviousClick() {
    this.queueMove(-1);
  }

  handleNextClick() {
    this.queueMove(1);
  }

  handleTouchStart(event) {
    const touch = event.changedTouches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
  }

  handleTouchEnd(event) {
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

    this.queueMove(deltaX < 0 ? 1 : -1);
  }

  handleBlockSelect(event) {
    const slide = event.target instanceof Element ? event.target.closest('[data-slide]') : null;
    if (!slide || !this.contains(slide)) return;

    const slideIndex = this.slides.indexOf(slide);
    if (slideIndex < 0) return;

    this.pendingIndexes.length = 0;
    window.scrollTo(0, this.getScrollTopForIndex(slideIndex));
    this.requestUpdate();
  }

  disconnectedCallback() {
    if (!this.isInitialized) return;

    this.isInitialized = false;
    this.previousButton.removeEventListener('click', this.handlePreviousClick);
    this.nextButton.removeEventListener('click', this.handleNextClick);
    this.viewport.removeEventListener('touchstart', this.handleTouchStart);
    this.viewport.removeEventListener('touchend', this.handleTouchEnd);
    window.removeEventListener('scroll', this.requestUpdate);
    window.removeEventListener('resize', this.scheduleMeasure);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    [this.rafId, this.initFrameId, this.moveAnimationId].forEach((frameId) => {
      if (frameId) cancelAnimationFrame(frameId);
    });
    clearTimeout(this.resizeTimer);

    this.rafId = null;
    this.initFrameId = null;
    this.moveAnimationId = null;
    this.resizeTimer = null;
    this.pendingIndexes = [];
    this.isProcessingMoves = false;

    ['--yg-card-width', '--yg-sticky-top', '--yg-sticky-height', '--yg-header-height', '--yg-media-height'].forEach(
      (property) => this.style.removeProperty(property)
    );
    this.scrollSpace.style.removeProperty('height');
    this.track.style.removeProperty('transform');
  }
}

if (!customElements.get('yg-time-box-swiper')) {
  customElements.define('yg-time-box-swiper', YgTimeBoxSwiper);
}
